import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { map, catchError } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { ApiService, TokenService } from '../../api';
import { EnvironmentService } from '../../services/environment.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { TokenResponse } from '../../api/interfaces/token.response';
import { AuthenticationService } from './authentication.service';
import { AuthenticationUserResponse } from '../interfaces/authentication-user.response';
import { LoginBase } from '../models';

/**
 * This service handles the authentication methods.
 *
 * @export
 * @class UserAuthenticationService
 */
@Injectable({
  providedIn: 'root',
})
export class JwtAuthenticationService<T = AuthenticationUserResponse> extends AuthenticationService<
  T
> {
  constructor(
    readonly environmentService: EnvironmentService,
    readonly tokenService: TokenService,
    readonly apiService: ApiService,
    readonly router: Router,
    readonly http: HttpClient,
  ) {
    super(environmentService, tokenService, apiService, router, http);
  }

  /**
   * This function registrate one user to the API.
   *
   * @param {{
   *     firstName: string;
   *     lastName: string;
   *     email: string;
   *     password: string;
   *     passwordConfirmation: string;
   *   }} user
   * @returns {Observable<boolean>}
   * @memberof UserAuthenticationService
   */
  public registrateUser(user: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    passwordConfirmation: string;
  }): Observable<boolean> {
    const params = new HttpParams();
    params.append('first_name', user.firstName);
    params.append('last_name', user.lastName);
    params.append('email', user.email);
    params.append('password', user.password);
    params.append('password_confirmation', user.passwordConfirmation);

    return this.http
      .post<TokenResponse>(`${this.environment.apiUrl}/register`, params.toString(), {
        headers: this.header,
      })
      .pipe(map(res => this.handleSuccess(res)));
  }

  /**
   * This function gains the Tokens from the JWT api.
   *
   * @param {LoginData} loginData
   * @returns {Observable<boolean>}
   * @memberof UserAuthenticationService
   */
  public obtainAccessToken(
    loginData: LoginBase,
    navigate = true,
    params = {},
  ): Observable<boolean> {
    return this.http
      .post<TokenResponse>(`${this.environment.apiUrl}/login`, {
        email: loginData.email,
        password: loginData.password,
      })
      .pipe(
        map((res: TokenResponse) => this.tokenResponseHandler(res, navigate, params)),
        catchError(err => {
          console.error(err);
          throw err;
        }),
      );
  }

  /**
   * This function resets the Access token.
   *
   * @private
   * @param {string} token
   * @returns {Observable<boolean>}
   * @memberof UserAuthenticationService
   */
  protected refreshAccessToken(): Observable<boolean> {
    return this.http
      .get<TokenResponse>(`${this.environment.apiUrl}/refresh`, {
        headers: {
          Authorization: `Bearer ${this.tokenService.getToken()}`,
        },
      })
      .pipe(
        map((res: TokenResponse) => this.tokenResponseHandler(res)),
        catchError(err => {
          console.error(err);
          throw err;
        }),
      );
  }

  private tokenResponseHandler(res: TokenResponse, navigate = true, params = {}) {
    res.refresh_token = res.access_token;
    this.tokenService.setToken(res);
    this.initialize(navigate, params);
    return true;
  }
}
